import type { Comment } from '@kizen/api/comment';
import type { AxiosInstance } from 'axios';
import type { paths, components } from '../schema';
import type { BulkActionSummaryInitializeRequest } from './bulk-actions';
import type { RequestOptions, RequestOptionsWithParams } from './types';
import { curry1, stringifyParams } from './utils';

type Option<T = string> = {
  label: string;
  value: T;
};

type Column =
  | Extract<
      keyof CustomerNotification,
      'actions' | 'created' | 'count' | 'notification_type' | 'title'
    >
  | 'status'
  | 'folder';

type Filter =
  | {
      input_type: 'singleselect';
      label: string;
      placeholder: string;
      name: 'comments';
      options: Option<'has_comments' | 'is_me'>[];
    }
  | {
      input_type: 'singleselect';
      label: string;
      placeholder: string;
      name: 'read_status';
      options: Option<'read' | 'unread'>[];
    }
  | {
      input_type: 'singleselect';
      label: string;
      placeholder: string;
      name: 'scope';
      options: Option<'business' | 'employee'>[];
    }
  | {
      input_type: 'multiselect';
      label: string;
      placeholder: string;
      name: 'notification_type';
      options: Option<
        | Exclude<
            CustomerNotification['notification_type'],
            'bulk_action_started' | 'bulk_action_finished'
          >
        | 'bulk_action_finished,bulk_action_started'
      >[];
    }
  | {
      input_type: 'date_range_inclusive';
      label: string;
      name: 'created';
    }
  | {
      input_type: 'multiselect';
      label: string;
      name: 'status';
      options: Option<
        'completed' | 'error' | 'info' | 'in_progress' | 'warning'
      >[];
      placeholder: string;
    };

type Action =
  | {
      type: 'download_file';
      label: string;
      url: string;
    }
  | {
      type: 'resend_webhook';
      activity_id: string;
      logged_activity_id: string;
    }
  | {
      type: 'view_automation';
      automation_id: string;
    }
  | {
      type: 'view_automation_affected_records';
      url: string;
    }
  | {
      type: 'view_automation_failures';
      url: string;
    }
  | {
      type: 'view_broadcast';
      scheduled_event_id: string;
    };

export type CustomerNotificationFilter<
  T extends Filter['name'] | undefined = undefined,
> = T extends undefined ? Filter : Extract<Filter, { name: T }>;

export type CustomerNotificationMetadata = {
  action_icons: Record<Action['type'], string>;
  action_labels: Record<Action['type'], string>;
  completed_notification_types: Array<
    CustomerNotification['notification_type']
  >;
  filters: Filter[];
  primary_actions: Record<
    CustomerNotification['notification_type'],
    Action['type']
  >;
  table: {
    columns: Column[];
    labels: Record<Column | CustomerNotification['notification_type'], string>;
    widths: Record<Column, string>;
  };
};

export type CustomerNotificatonBulkAction = Extract<
  BulkActionSummaryInitializeRequest,
  { action: `notifications_${string}` }
>;

export interface CustomerNotification
  extends Omit<
    Mutable<components['schemas']['CustomerNotificationOutput']>,
    'actions' | 'archived' | 'count' | 'read' | 'scope'
  > {
  actions: Action[];
  archived: boolean;
  count: number;
  read: boolean;
  scope: 'BUSINESS' | 'EMPLOYEE';
}

export interface PaginatedCustomerNotificationList
  extends Omit<
    components['schemas']['PaginatedCustomerNotificationOutputList'],
    'next_cursor' | 'prev_cursor' | 'results'
  > {
  results: CustomerNotification[];
  next_cursor: string | null;
  prev_cursor: string | null;
}

type SchemaCustomerNotificationParams = Exclude<
  paths['/api/customer-notifications/']['get']['parameters']['query'],
  'notification_type' | undefined
>;
export interface CustomerNotificationParams
  extends Omit<
    SchemaCustomerNotificationParams,
    'notification_type' | 'status'
  > {
  notification_type?: Array<
    CustomerNotificationFilter<'notification_type'>['options'][number]['value']
  >;
  status?: Array<
    CustomerNotificationFilter<'status'>['options'][number]['value']
  >;
}

export type CustomerNotificationIndicatorResponse = {
  status: 'in_progress' | 'error' | 'warning' | 'info' | 'none';
};

export const archiveCustomerNotifications = async (
  instance: AxiosInstance,
  data: paths['/api/customer-notifications/bulk-actions/archive']['post']['requestBody']['content']['application/json'],
  options: RequestOptions = {}
): Promise<void> => {
  const res = await instance.post(
    '/customer-notifications/bulk-actions/archive',
    data,
    options
  );

  return res.data;
};

export const getCustomerNotifications = async (
  instance: AxiosInstance,
  options: RequestOptionsWithParams<CustomerNotificationParams> = {}
): Promise<PaginatedCustomerNotificationList> => {
  const { params, ...rest } = options;

  const res = await instance.get('/customer-notifications/', {
    params: stringifyParams(params),
    ...rest,
  });

  return res.data;
};

export const markCustomerNotificationsRead = async (
  instance: AxiosInstance,
  data: paths['/api/customer-notifications/bulk-actions/read']['post']['requestBody']['content']['application/json'],
  options: RequestOptions = {}
): Promise<void> => {
  const res = await instance.post(
    '/customer-notifications/bulk-actions/read',
    data,
    options
  );

  return res.data;
};

export const getCustomerNotificationComments = async (
  instance: AxiosInstance,
  id: string,
  options: RequestOptions = {}
): Promise<Comment[]> => {
  const res = await instance.get(
    `/customer-notifications/${id}/comments`,
    options
  );

  return res.data;
};

export const getCustomerNotificationIndicator = async (
  instance: AxiosInstance,
  options: RequestOptions = {}
): Promise<CustomerNotificationIndicatorResponse> => {
  const res = await instance.get('/customer-notifications/indicator', options);

  return res.data;
};

export const getCustomerNotificationMetadata = async (
  instance: AxiosInstance,
  options: RequestOptions = {}
): Promise<CustomerNotificationMetadata> => {
  const res = await instance.get('/customer-notifications/metadata', options);

  return res.data;
};

export class CustomerNotificationsService {
  public archiveCustomerNotifications;
  public getCustomerNotifications;
  public getCustomerNotificationComments;
  public getCustomerNotificationIndicator;
  public getCustomerNotificationMetadata;
  public markCustomerNotificationsRead;

  constructor(private instance: AxiosInstance) {
    this.archiveCustomerNotifications = curry1(archiveCustomerNotifications)(
      this.instance
    );
    this.getCustomerNotifications = curry1(getCustomerNotifications)(
      this.instance
    );
    this.getCustomerNotificationComments = curry1(
      getCustomerNotificationComments
    )(this.instance);
    this.getCustomerNotificationIndicator = curry1(
      getCustomerNotificationIndicator
    )(this.instance);
    this.getCustomerNotificationMetadata = curry1(
      getCustomerNotificationMetadata
    )(this.instance);
    this.markCustomerNotificationsRead = curry1(markCustomerNotificationsRead)(
      this.instance
    );
  }
}
